import type { Translate, TranslationQuery } from 'next-translate';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslationContext } from '@/context/translation';
import { getLocaleFromPath } from '@/helpers/getLocaleFromPath';
import { getTranslationByKey } from '@/helpers/getTranslationByKey';
import { DEFAULT_LOCALE } from '@/constants/locales';

/**
 * In-memory cache for translations.
 * Added TTL + eviction to prevent unbounded growth on long sessions.
 *
 * - TTL (staleTime): entries older than STALE_TIME_MS are treated as stale and refetched.
 * - Eviction (cacheTime / max size): entries are periodically cleaned and the cache is capped.
 */

interface CacheEntry {
	data: Record<string, unknown>;
	updatedAt: number; // ms since epoch
	lastAccessAt: number; // ms since epoch
}

const STALE_TIME_MS = 10 * 60 * 1000; // 10 minutes: treat as stale and refetch
const CACHE_TIME_MS = 60 * 60 * 1000; // 60 minutes: evict if not accessed
const MAX_CACHE_ENTRIES = 120; // cap to prevent memory growth (tune if needed)

const translationCache: Record<string, CacheEntry> = Object.create(null);

const now = () => Date.now();

const getCacheEntry = (key: string): CacheEntry | undefined => {
	const entry = translationCache[key];
	if (!entry) return undefined;
	entry.lastAccessAt = now();
	return entry;
};

const isStale = (entry: CacheEntry) => now() - entry.updatedAt > STALE_TIME_MS;

const setCacheEntry = (key: string, data: Record<string, unknown>) => {
	const t = now();
	translationCache[key] = {
		data,
		updatedAt: t,
		lastAccessAt: t,
	};
};

const purgeCache = () => {
	const t = now();

	// 1) Remove entries not accessed within CACHE_TIME_MS
	for (const [key, entry] of Object.entries(translationCache)) {
		if (t - entry.lastAccessAt > CACHE_TIME_MS) {
			// eslint-disable-next-line @typescript-eslint/no-dynamic-delete
			delete translationCache[key];
		}
	}

	// 2) Enforce max size: remove least recently accessed
	const keys = Object.keys(translationCache);
	if (keys.length <= MAX_CACHE_ENTRIES) return;

	keys.sort(
		(a, b) =>
			// eslint-disable-next-line no-unsafe-optional-chaining
			translationCache[a]?.lastAccessAt - translationCache[b]?.lastAccessAt,
	);

	const toRemove = keys.length - MAX_CACHE_ENTRIES;
	for (let i = 0; i < toRemove; i++) {
		// eslint-disable-next-line @typescript-eslint/no-dynamic-delete
		delete translationCache[keys[i]];
	}
};

// Throttle purge so we don't run cleanup too often
let lastPurgeAt = 0;
const maybePurgeCache = () => {
	const t = now();
	if (t - lastPurgeAt < 30_000) return; // at most once per 30s
	lastPurgeAt = t;
	purgeCache();
};

export const useTranslationWithLocale = (
	namespace: string | undefined,
	initialLocale?: string,
	serverTranslations?: Record<string, unknown>,
) => {
	const router = useRouter();
	const effectiveNamespace = namespace ?? '';
	const { translations: contextTranslations, locale: contextLocale } =
		useTranslationContext();

	const namespaceTranslationsFromContext =
		contextTranslations[effectiveNamespace];

	// Determine initial translations: serverTranslations > context > empty
	const getInitialTranslations = () => {
		if (serverTranslations && Object.keys(serverTranslations).length > 0) {
			return serverTranslations;
		}
		if (
			namespaceTranslationsFromContext &&
			Object.keys(namespaceTranslationsFromContext).length > 0
		) {
			return namespaceTranslationsFromContext;
		}
		return {};
	};

	const initialTranslations = getInitialTranslations();
	const [translations, setTranslations] =
		useState<Record<string, unknown>>(initialTranslations);
	const [isLoading, setIsLoading] = useState(
		Object.keys(initialTranslations).length === 0,
	);

	// Determine target locale:
	// 1. Use initialLocale if provided
	// 2. Use contex tLocale only if it's not default 'en' or if we have translations in context
	// 3. Otherwise, determine from router/cookies
	const hasContextTranslations =
		contextTranslations && Object.keys(contextTranslations).length > 0;
	const effectiveContextLocale =
		contextLocale &&
		(contextLocale !== DEFAULT_LOCALE || hasContextTranslations)
			? contextLocale
			: undefined;

	const getClientPathLocale = () => {
		if (typeof window === 'undefined') return null;
		return getLocaleFromPath(window.location.pathname || '/');
	};

	// URL is the single source of truth on the client.
	// On the server we still use initial/context locale for first SSR render.
	const computedLocale =
		getClientPathLocale() ??
		initialLocale ??
		effectiveContextLocale ??
		DEFAULT_LOCALE;

	const [targetLocale, setTargetLocale] = useState(computedLocale);

	// Refs for stable translate
	const translationsRef = useRef(translations);
	const isLoadingRef = useRef(isLoading);
	const targetLocaleRef = useRef(targetLocale);
	const effectiveNamespaceRef = useRef(effectiveNamespace);
	const contextTranslationsRef = useRef(namespaceTranslationsFromContext);
	const requestIdRef = useRef(0);

	translationsRef.current = translations;
	isLoadingRef.current = isLoading;
	targetLocaleRef.current = targetLocale;
	effectiveNamespaceRef.current = effectiveNamespace;
	contextTranslationsRef.current = namespaceTranslationsFromContext;

	// Update locale when router becomes ready or locale changes
	useEffect(() => {
		const newLocale =
			getClientPathLocale() ??
			initialLocale ??
			effectiveContextLocale ??
			DEFAULT_LOCALE;

		if (newLocale !== targetLocale) {
			setTargetLocale(newLocale);
		}
	}, [initialLocale, effectiveContextLocale, router.asPath, targetLocale]);

	// Sync translations from context when they become available (only when context locale matches target)
	useEffect(() => {
		if (
			namespaceTranslationsFromContext &&
			Object.keys(namespaceTranslationsFromContext).length > 0 &&
			contextLocale === targetLocale
		) {
			const shouldSync =
				Object.keys(translations).length === 0 ||
				!serverTranslations ||
				Object.keys(serverTranslations).length === 0 ||
				Object.keys(namespaceTranslationsFromContext).length >
					Object.keys(translations).length;

			if (shouldSync) {
				setTranslations(namespaceTranslationsFromContext);
				setIsLoading(false);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [namespaceTranslationsFromContext, contextLocale, targetLocale]);

	// Also check on mount if context translations are available but weren't used initially.
	useEffect(() => {
		if (
			namespaceTranslationsFromContext &&
			Object.keys(namespaceTranslationsFromContext).length > 0 &&
			Object.keys(translations).length === 0 &&
			contextLocale === targetLocale
		) {
			setTranslations(namespaceTranslationsFromContext);
			setIsLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Sync serverTranslations when they become available (only when server locale matches target)
	useEffect(() => {
		if (
			serverTranslations &&
			Object.keys(serverTranslations).length > 0 &&
			initialLocale === targetLocale
		) {
			if (
				Object.keys(translations).length === 0 ||
				Object.keys(serverTranslations).length >
					Object.keys(translations).length
			) {
				setTranslations(serverTranslations);
				setIsLoading(false);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [serverTranslations, initialLocale, targetLocale]);

	useEffect(() => {
		const requestId = ++requestIdRef.current;
		const controller = new AbortController();
		const { signal } = controller;

		const applyIfCurrent = (fn: () => void) => {
			if (requestIdRef.current === requestId) fn();
		};

		// Run occasional cache cleanup
		maybePurgeCache();

		// If server translations were provided and we have them in state, skip client-side loading
		if (
			serverTranslations &&
			Object.keys(serverTranslations).length > 0 &&
			targetLocale === initialLocale
		) {
			return () => controller.abort();
		}

		// If translations are already in context for this locale, use them and skip API call.
		if (
			namespaceTranslationsFromContext &&
			Object.keys(namespaceTranslationsFromContext).length > 0 &&
			contextLocale === targetLocale
		) {
			applyIfCurrent(() => {
				setTranslations(namespaceTranslationsFromContext);
				setIsLoading(false);
			});
			return () => controller.abort();
		}

		// Skip API call when namespace is missing to avoid /api/translations/en/undefined
		if (!effectiveNamespace) {
			applyIfCurrent(() => setIsLoading(false));
			return () => controller.abort();
		}

		const cacheKey = `${targetLocale}/${effectiveNamespace}`;
		const enCacheKey = `${DEFAULT_LOCALE}/${effectiveNamespace}`;

		const cached = getCacheEntry(cacheKey);
		if (cached && !isStale(cached)) {
			applyIfCurrent(() => {
				setTranslations(cached.data);
				setIsLoading(false);
			});
			return () => controller.abort();
		}

		const loadTranslations = async () => {
			try {
				// We may already have stale cached data; use it immediately while refetching.
				if (cached?.data && Object.keys(cached.data).length > 0) {
					applyIfCurrent(() => {
						setTranslations(cached.data);
						setIsLoading(true); // keep loading true while we refetch
					});
				}

				const enCached = getCacheEntry(enCacheKey);
				const needEn =
					targetLocale !== DEFAULT_LOCALE && (!enCached || isStale(enCached));
				const needTarget = !cached || isStale(cached);

				// If we don't need to refetch target (i.e., stale check passed), we would have returned above.
				// Here: needTarget is true.
				if (needEn && needTarget) {
					const [enSettled, targetSettled] = await Promise.allSettled([
						fetch(`/api/translations/${DEFAULT_LOCALE}/${effectiveNamespace}`, {
							signal,
							cache: 'no-store',
						}),
						fetch(`/api/translations/${targetLocale}/${effectiveNamespace}`, {
							signal,
							cache: 'no-store',
						}),
					]);

					if (enSettled.status === 'fulfilled' && enSettled.value.ok) {
						const enData = (await enSettled.value.json()) as Record<
							string,
							unknown
						>;
						setCacheEntry(enCacheKey, enData);
					}

					if (targetSettled.status === 'fulfilled' && targetSettled.value.ok) {
						const translationData =
							(await targetSettled.value.json()) as Record<string, unknown>;
						setCacheEntry(cacheKey, translationData);

						applyIfCurrent(() => {
							setTranslations(translationData);
							setIsLoading(false);
						});
					} else {
						const fallback =
							getCacheEntry(cacheKey)?.data ?? getCacheEntry(enCacheKey)?.data;
						applyIfCurrent(() => {
							if (fallback && Object.keys(fallback).length > 0)
								setTranslations(fallback);
							setIsLoading(false);
						});
					}
					return;
				}

				if (needEn) {
					try {
						const enResponse = await fetch(
							`/api/translations/${DEFAULT_LOCALE}/${effectiveNamespace}`,
							{ signal, cache: 'no-store' },
						);
						if (enResponse.ok) {
							const enData = (await enResponse.json()) as Record<
								string,
								unknown
							>;
							setCacheEntry(enCacheKey, enData);
						}
					} catch {
						// Ignore (abort or network)
					}
				}

				const response = await fetch(
					`/api/translations/${targetLocale}/${effectiveNamespace}`,
					{ signal, cache: 'no-store' },
				);

				if (response.ok) {
					const translationData = (await response.json()) as Record<
						string,
						unknown
					>;
					setCacheEntry(cacheKey, translationData);

					applyIfCurrent(() => {
						setTranslations(translationData);
						setIsLoading(false);
					});
				} else {
					const fallback = getCacheEntry(enCacheKey)?.data;
					applyIfCurrent(() => {
						if (fallback && Object.keys(fallback).length > 0)
							setTranslations(fallback);
						setIsLoading(false);
					});
				}
			} catch (err) {
				if ((err as Error).name === 'AbortError') return;

				applyIfCurrent(() => {
					if (
						serverTranslations &&
						Object.keys(serverTranslations).length > 0
					) {
						setTranslations(serverTranslations);
					} else if (
						namespaceTranslationsFromContext &&
						Object.keys(namespaceTranslationsFromContext).length > 0
					) {
						setTranslations(namespaceTranslationsFromContext);
					} else {
						const fallback = getCacheEntry(enCacheKey)?.data;
						if (fallback && Object.keys(fallback).length > 0)
							setTranslations(fallback);
					}
					setIsLoading(false);
				});
			} finally {
				// cleanup occasionally after request completes
				maybePurgeCache();
			}
		};

		void loadTranslations();

		return () => {
			controller.abort();
		};
	}, [
		targetLocale,
		effectiveNamespace,
		serverTranslations,
		initialLocale,
		contextLocale,
		namespaceTranslationsFromContext,
		router.isReady,
	]);

	const translate: Translate = useMemo(() => {
		return (
			key: string | readonly string[],
			query?: TranslationQuery | null,
		) => {
			const keyString =
				// eslint-disable-next-line no-nested-ternary
				typeof key === 'string'
					? key
					: Array.isArray(key)
					? key.join('')
					: String(key);

			const currentTranslations = translationsRef.current;
			const currentContext = contextTranslationsRef.current as
				| Record<string, unknown>
				| undefined;
			const currentLocale = targetLocaleRef.current;
			const currentNs = effectiveNamespaceRef.current;
			const loading = isLoadingRef.current;

			let currentValue = getTranslationByKey(currentTranslations, keyString);

			if (currentValue === null && currentContext) {
				currentValue = getTranslationByKey(currentContext, keyString);
			}

			const enKey = `${DEFAULT_LOCALE}/${currentNs}`;
			const enCached = getCacheEntry(enKey);

			if (
				currentValue === null &&
				currentLocale !== DEFAULT_LOCALE &&
				enCached?.data
			) {
				currentValue = getTranslationByKey(enCached.data, keyString);
			}

			// When no translation found: show English from cache if loading, otherwise show key
			if (currentValue === null) {
				if (loading && enCached?.data) {
					const enValue = getTranslationByKey(enCached.data, keyString);
					if (enValue) currentValue = enValue;
				}
				if (currentValue === null) return keyString;
			}

			if (query) {
				return currentValue.replace(/\{\{(\w+)\}\}/g, (match, paramKey) => {
					const value = query[paramKey];
					return value?.toString() ?? match;
				});
			}

			return currentValue;
		};
	}, []) as Translate;

	return { t: translate, lang: targetLocale, isLoading };
};
